import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Plus, Minus, Building2, Link } from 'lucide-react';
import { toast } from 'sonner';
import { useUser } from '../context/UserContext';
import { 
  ActivityContactsSelector, 
  ActivityCompanySelector, 
  ActivityDynamicFields,
  ActivityAIFields,
  activityTypes,
  typeLabels,
  typeFields,
  fieldLabels,
  companyCategories,
  typsaOrgCategories,
  fieldPlaceholders
} from './activity';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ActivityDialog = ({ open, onOpenChange, companyId, contactId, opportunityId, typsaOrgUnitId, typsaCategory, activity, onSuccess }) => {
  const { currentUser, editorUsers } = useUser();
  
  // Form data state
  const [formData, setFormData] = useState({
    title: '',
    type: 'meeting',
    date: '',
    company_id: companyId || '',
    contact_ids: contactId ? [contactId] : [''],
    opportunity_id: opportunityId || '',
    owner: '',
    result_data: {},
    summary: '',
    notes: '',
    typsa_category: '',
    typsa_category_value: '',
    typsa_org_unit_id: '',
    // AI-extracted fields
    key_points: [],
    decisions: [],
    next_steps: [],
    risks_concerns: [],
    opportunity_signals: [],
    transcript: '',
    voice_note_id: ''
  });
  
  // Company selection state
  const [companyCategory, setCompanyCategory] = useState('');
  const [typsaOrgCategory, setTypsaOrgCategory] = useState('');
  const [typsaOrgUnits, setTypsaOrgUnits] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [opportunities, setOpportunities] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Quick create company state
  const [showCreateCompany, setShowCreateCompany] = useState(false);
  const [newCompanyName, setNewCompanyName] = useState('');
  const [creatingCompany, setCreatingCompany] = useState(false);

  // Fetch related data
  const fetchRelatedData = async () => {
    try {
      const [companiesRes, contactsRes, oppsRes, typsaRes] = await Promise.all([
        axios.get(`${API}/companies`),
        axios.get(`${API}/contacts`),
        axios.get(`${API}/opportunities`),
        axios.get(`${API}/typsa-org-units`)
      ]);
      setCompanies(companiesRes.data);
      setContacts(contactsRes.data);
      setOpportunities(oppsRes.data);
      setTypsaOrgUnits(typsaRes.data);
    } catch (error) {
      console.error('Error fetching related data:', error);
    }
  };

  useEffect(() => {
    if (open) fetchRelatedData();
  }, [open]);

  // Initialize form from activity or reset
  useEffect(() => {
    if (!open) return;
    
    if (activity) {
      let contactIds = activity.contact_ids?.length > 0 
        ? activity.contact_ids 
        : activity.contact_id ? [activity.contact_id] : [''];
      
      const resultData = parseExistingData(activity.summary, activity.notes, activity.type);
      
      // Parse AI fields from summary if not already in dedicated fields
      const parsedAIFields = parseAIFieldsFromSummary(activity.summary);
      
      setFormData({
        title: activity.title || '',
        type: activity.type || 'meeting',
        date: activity.date || '',
        company_id: activity.company_id || companyId || '',
        contact_ids: contactIds,
        opportunity_id: activity.opportunity_id || opportunityId || '',
        owner: activity.owner || '',
        result_data: resultData,
        summary: activity.summary || '',
        notes: activity.notes || '',
        typsa_category: activity.typsa_category || '',
        typsa_category_value: activity.typsa_category_value || '',
        typsa_org_unit_id: activity.typsa_org_unit_id || '',
        // AI fields - use dedicated fields if available, otherwise parse from summary
        key_points: activity.key_points?.length ? activity.key_points : parsedAIFields.key_points,
        decisions: activity.decisions?.length ? activity.decisions : parsedAIFields.decisions,
        next_steps: activity.next_steps?.length ? activity.next_steps : parsedAIFields.next_steps,
        risks_concerns: activity.risks_concerns?.length ? activity.risks_concerns : parsedAIFields.risks_concerns,
        opportunity_signals: activity.opportunity_signals?.length ? activity.opportunity_signals : parsedAIFields.opportunity_signals,
        transcript: activity.transcript || activity.notes || '',
        voice_note_id: activity.voice_note_id || '',
        content_link: activity.content_link || ''
      });
    } else {
      // Determinar la categoría inicial basándose en los props
      const initialCompanyCategory = typsaOrgUnitId ? 'typsa' : (companyId ? 'external' : '');
      
      setFormData({
        title: '',
        type: 'meeting',
        date: new Date().toISOString().split('T')[0],
        company_id: companyId || '',
        contact_ids: contactId ? [contactId] : [''],
        opportunity_id: opportunityId || '',
        owner: currentUser?.name || '',
        result_data: {},
        summary: '',
        notes: '',
        typsa_category: typsaCategory || '',
        typsa_category_value: '',
        typsa_org_unit_id: typsaOrgUnitId || '',
        // AI fields
        key_points: [],
        decisions: [],
        next_steps: [],
        risks_concerns: [],
        opportunity_signals: [],
        transcript: '',
        voice_note_id: '',
        content_link: ''
      });
      setCompanyCategory(initialCompanyCategory);
      setTypsaOrgCategory(typsaCategory || '');
    }
  }, [activity, companyId, contactId, opportunityId, typsaOrgUnitId, typsaCategory, open, currentUser]);

  // Set company category when companies/typsaOrgUnits are loaded
  useEffect(() => {
    if (!open) return;
    
    // For editing existing activity
    if (activity) {
      if (activity.typsa_org_unit_id && typsaOrgUnits.length > 0) {
        // Activity is linked to a TYPSA organizational unit
        setCompanyCategory('typsa');
        const orgUnit = typsaOrgUnits.find(u => u.id === activity.typsa_org_unit_id);
        if (orgUnit) {
          setTypsaOrgCategory(orgUnit.category || activity.typsa_category || '');
        }
      } else if (activity.company_id && companies.length > 0) {
        // Activity is linked to a company
        const company = companies.find(c => c.id === activity.company_id);
        if (company) {
          // Check if it's a TYPSA company but stored as regular company
          if (company.company_type === 'typsa') {
            // It's a TYPSA entity stored as company - find corresponding org unit
            const matchingOrgUnit = typsaOrgUnits.find(u => 
              u.name === company.name || u.value === company.name
            );
            if (matchingOrgUnit) {
              setCompanyCategory('typsa');
              setTypsaOrgCategory(matchingOrgUnit.category || '');
              // Update formData to use typsa_org_unit_id instead
              setFormData(prev => ({
                ...prev,
                company_id: '',
                typsa_org_unit_id: matchingOrgUnit.id
              }));
            } else {
              // No matching org unit, keep as company but mark as typsa type
              setCompanyCategory('typsa');
            }
          } else {
            setCompanyCategory(company.company_type || 'privada');
          }
        }
      }
    }
    
    // For new activity with companyId prop
    if (!activity && companyId && companies.length > 0) {
      const company = companies.find(c => c.id === companyId);
      if (company) {
        const companyType = company.company_type || 'privada';
        setCompanyCategory(companyType);
        console.log('Setting company category from prop:', companyType, 'for company:', company.name);
      }
    }
  }, [activity, open, companies, typsaOrgUnits, companyId]);

  // Parse AI fields from summary text
  const parseAIFieldsFromSummary = (summary) => {
    const result = {
      key_points: [],
      decisions: [],
      next_steps: [],
      risks_concerns: [],
      opportunity_signals: []
    };
    
    if (!summary) return result;
    
    // Try to parse structured format: **Label:**\ncontent
    const sections = {
      'puntos clave': 'key_points',
      'key points': 'key_points',
      'decisiones': 'decisions',
      'decisions': 'decisions',
      'próximos pasos': 'next_steps',
      'next steps': 'next_steps',
      'riesgos': 'risks_concerns',
      'risks': 'risks_concerns',
      'oportunidades': 'opportunity_signals',
      'opportunities': 'opportunity_signals'
    };
    
    // Match **Label:** followed by content
    const regex = /\*\*([^*]+):\*\*\s*([\s\S]*?)(?=\*\*[^*]+:\*\*|$)/gi;
    let match;
    
    while ((match = regex.exec(summary)) !== null) {
      const label = match[1].trim().toLowerCase();
      const content = match[2].trim();
      const fieldKey = sections[label];
      
      if (fieldKey && content) {
        // Split by bullet points or newlines
        const items = content
          .split(/(?:^|\n)\s*[•\-\*]\s*/)
          .map(item => item.trim())
          .filter(item => item.length > 0);
        
        if (items.length > 0) {
          result[fieldKey] = items;
        } else if (content) {
          result[fieldKey] = [content];
        }
      }
    }
    
    return result;
  };

  // Parse existing summary into result_data
  const parseExistingData = (summary, notes, type) => {
    const resultData = {};
    const fields = typeFields[type]?.fields || [];
    const labelToField = {};
    Object.entries(fieldLabels).forEach(([field, label]) => {
      labelToField[label.toLowerCase()] = field;
    });
    
    if (summary) {
      const sections = summary.split(/\*\*([^*]+):\*\*/);
      if (sections.length > 1) {
        for (let i = 1; i < sections.length; i += 2) {
          const label = sections[i].trim().toLowerCase();
          const content = sections[i + 1]?.trim() || '';
          const fieldKey = labelToField[label];
          if (fieldKey && fields.includes(fieldKey) && content) {
            resultData[fieldKey] = content;
          }
        }
      } else if (fields.includes('key_points')) {
        resultData.key_points = summary;
      } else if (fields[0]) {
        resultData[fields[0]] = summary;
      }
    }
    
    if (notes) {
      if (fields.includes('transcript') && !resultData.transcript) {
        resultData.transcript = notes;
      } else if (fields.includes('internal_notes') && !resultData.internal_notes) {
        resultData.internal_notes = notes;
      }
    }
    return resultData;
  };

  // Build summary for saving
  const buildSummaryAndNotes = () => {
    const fields = typeFields[formData.type]?.fields || [];
    const data = formData.result_data;
    const summaryParts = [];
    let notes = '';
    
    fields.forEach(field => {
      const value = data[field];
      if (!value) return;
      
      if (['transcript', 'internal_notes', 'event_description'].includes(field)) {
        notes = value;
      } else {
        const label = fieldLabels[field] || field;
        summaryParts.push(`**${label}:**\n${value}`);
      }
    });
    
    return { summary: summaryParts.join('\n\n'), notes };
  };

  // Filter functions
  const getFilteredCompanies = () => {
    if (!companyCategory || companyCategory === 'typsa') return [];
    return companies.filter(c => c.company_type === companyCategory);
  };

  const getFilteredTypsaUnits = () => {
    if (!typsaOrgCategory) return [];
    const normalizedCategory = typsaOrgCategory.toLowerCase().trim();
    return typsaOrgUnits.filter(u => 
      u.category && u.category.toLowerCase().trim() === normalizedCategory
    );
  };

  const getFilteredContacts = () => {
    if (formData.typsa_org_unit_id) {
      return contacts.filter(c => c.typsa_org_unit_id === formData.typsa_org_unit_id);
    }
    if (formData.company_id) {
      return contacts.filter(c => c.company_id === formData.company_id);
    }
    return contacts;
  };

  const getFilteredOpportunities = () => {
    if (formData.typsa_org_unit_id) {
      return opportunities.filter(o => o.typsa_org_unit_id === formData.typsa_org_unit_id);
    }
    if (formData.company_id) {
      return opportunities.filter(o => o.company_id === formData.company_id);
    }
    return [];
  };

  // Handlers
  const handleCompanyCategoryChange = (value) => {
    setCompanyCategory(value);
    setTypsaOrgCategory('');
    setFormData(prev => ({ ...prev, company_id: '', typsa_org_unit_id: '', contact_ids: [''], opportunity_id: '' }));
  };

  const handleTypsaOrgCategoryChange = (value) => {
    setTypsaOrgCategory(value);
    setFormData(prev => ({ ...prev, typsa_org_unit_id: '', contact_ids: [''], opportunity_id: '' }));
  };

  const handleCompanyChange = (value) => {
    setFormData(prev => ({ ...prev, company_id: value === 'none' ? '' : value, typsa_org_unit_id: '', contact_ids: [''], opportunity_id: '' }));
  };

  const handleTypsaUnitChange = (value) => {
    setFormData(prev => ({ ...prev, typsa_org_unit_id: value === 'none' ? '' : value, company_id: '', contact_ids: [''], opportunity_id: '' }));
  };

  const handleContactChange = (index, value) => {
    setFormData(prev => {
      const newIds = [...prev.contact_ids];
      newIds[index] = value === 'none' ? '' : value;
      return { ...prev, contact_ids: newIds };
    });
  };

  const addContactField = () => setFormData(prev => ({ ...prev, contact_ids: [...prev.contact_ids, ''] }));
  const removeContactField = (index) => setFormData(prev => ({ ...prev, contact_ids: prev.contact_ids.filter((_, i) => i !== index) }));

  const updateResultData = (field, value) => {
    setFormData(prev => ({ ...prev, result_data: { ...prev.result_data, [field]: value } }));
  };

  // Quick create company
  const handleCreateCompany = async () => {
    if (!newCompanyName.trim()) return toast.error('Nombre obligatorio');
    setCreatingCompany(true);
    try {
      const res = await axios.post(`${API}/companies`, { name: newCompanyName, company_type: companyCategory || 'privada', status: 'Prospecto', rol: 'Cliente' });
      toast.success('Empresa creada');
      const companiesRes = await axios.get(`${API}/companies`);
      setCompanies(companiesRes.data);
      setFormData(prev => ({ ...prev, company_id: res.data.id }));
      setShowCreateCompany(false);
      setNewCompanyName('');
    } catch (error) {
      toast.error('Error al crear empresa');
    } finally {
      setCreatingCompany(false);
    }
  };

  // Handler for AI fields
  const handleAIFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Submit
  const handleSubmit = async () => {
    if (!formData.title.trim()) return toast.error('Título obligatorio');
    if (!formData.date) return toast.error('Fecha obligatoria');

    setLoading(true);
    try {
      const { summary, notes } = buildSummaryAndNotes();
      const payload = {
        title: formData.title,
        type: formData.type,
        date: formData.date,
        company_id: formData.company_id || null,
        typsa_org_unit_id: formData.typsa_org_unit_id || null,
        contact_ids: formData.contact_ids.filter(id => id),
        opportunity_id: formData.opportunity_id || null,
        owner: formData.owner,
        summary: summary || formData.summary,
        notes: notes || formData.notes,
        typsa_category: formData.typsa_category,
        typsa_category_value: formData.typsa_category_value,
        // Resource link
        content_link: formData.content_link || null,
        // AI-extracted fields
        key_points: formData.key_points?.length ? formData.key_points.filter(p => p) : null,
        decisions: formData.decisions?.length ? formData.decisions.filter(d => d) : null,
        next_steps: formData.next_steps?.length ? formData.next_steps.filter(s => s) : null,
        risks_concerns: formData.risks_concerns?.length ? formData.risks_concerns.filter(r => r) : null,
        opportunity_signals: formData.opportunity_signals?.length ? formData.opportunity_signals.filter(o => o) : null,
        transcript: formData.transcript || null,
        voice_note_id: formData.voice_note_id || null
      };

      if (activity) {
        await axios.put(`${API}/activities/${activity.id}`, payload);
        toast.success('Actividad actualizada');
      } else {
        await axios.post(`${API}/activities`, payload, { headers: { 'x-user-email': currentUser?.email } });
        toast.success('Actividad creada');
      }
      onSuccess?.();
      onOpenChange?.(false);
    } catch (error) {
      toast.error('Error: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto" data-testid="activity-dialog">
        <DialogHeader>
          <DialogTitle>{activity ? 'Editar Actividad' : 'Nueva Actividad'}</DialogTitle>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Title */}
          <div className="grid gap-2">
            <Label>Título *</Label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
              placeholder="Título de la actividad"
              data-testid="activity-title-input"
            />
          </div>

          {/* Type and Date */}
          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>Tipo</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData(prev => ({ ...prev, type: v, result_data: {} }))}>
                <SelectTrigger data-testid="activity-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {activityTypes.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid gap-2">
              <Label>Fecha *</Label>
              <Input type="date" value={formData.date} onChange={(e) => setFormData(prev => ({ ...prev, date: e.target.value }))} data-testid="activity-date-input" />
            </div>
          </div>

          {/* Company Selector */}
          <ActivityCompanySelector
            companyCategory={companyCategory}
            onCompanyCategoryChange={handleCompanyCategoryChange}
            typsaOrgCategory={typsaOrgCategory}
            onTypsaOrgCategoryChange={handleTypsaOrgCategoryChange}
            companyId={formData.company_id}
            onCompanyChange={handleCompanyChange}
            typsaOrgUnitId={formData.typsa_org_unit_id}
            onTypsaOrgUnitChange={handleTypsaUnitChange}
            companies={companies}
            typsaOrgUnits={typsaOrgUnits}
            showCreateCompany={showCreateCompany}
            onShowCreateCompanyChange={setShowCreateCompany}
            newCompanyName={newCompanyName}
            onNewCompanyNameChange={setNewCompanyName}
            onCreateCompany={handleCreateCompany}
            creatingCompany={creatingCompany}
          />

          {/* Contacts */}
          <ActivityContactsSelector
            contactIds={formData.contact_ids}
            contacts={contacts}
            companyId={formData.company_id}
            typsaOrgUnitId={formData.typsa_org_unit_id}
            onChange={handleContactChange}
            onAdd={addContactField}
            onRemove={removeContactField}
          />

          {/* Opportunity */}
          {(formData.company_id || formData.typsa_org_unit_id) && (
            <div className="grid gap-2">
              <Label>Oportunidad</Label>
              <Select value={formData.opportunity_id || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, opportunity_id: v === 'none' ? '' : v }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Sin oportunidad" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Sin oportunidad</SelectItem>
                  {getFilteredOpportunities().map(o => <SelectItem key={o.id} value={o.id}>{o.title} ({o.stage})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Owner */}
          <div className="grid gap-2">
            <Label>Responsable</Label>
            <Select value={formData.owner || 'none'} onValueChange={(v) => setFormData(prev => ({ ...prev, owner: v === 'none' ? '' : v }))}>
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar responsable" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Sin asignar</SelectItem>
                {editorUsers?.map(u => <SelectItem key={u.email} value={u.name}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>

          {/* Content Link */}
          <div className="grid gap-2">
            <Label className="flex items-center gap-2">
              <Link className="w-4 h-4" />
              Enlace a contenido
            </Label>
            <Input
              type="url"
              placeholder="https://drive.google.com/... o URL del documento"
              value={formData.content_link || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, content_link: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">URL a presentación, documento o contenido relacionado</p>
          </div>

          {/* AI Fields - Show always for activities, especially if from voice note */}
          <ActivityAIFields
            formData={formData}
            onChange={handleAIFieldChange}
            showTranscript={true}
            isFromVoiceNote={!!formData.voice_note_id}
            currentUser={currentUser}
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={loading} data-testid="activity-submit-btn">
            {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Guardando...</> : activity ? 'Actualizar' : 'Crear'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ActivityDialog;
